import { Component, define } from '@theme/component';

/**
 * The fixed header and its full-screen menu.
 *
 * Three behaviours, all from the WordPress site:
 *
 *   - The menu button opens the menu over the whole page. The grey fills the
 *     screen from the button outward and the links fade in after it, which is
 *     all CSS keyed on `data-menu-open`; this only flips the attribute, moves
 *     focus, and closes again on Esc, on a link and on the button.
 *   - Once the page has scrolled, `data-scrolled` lays a gradient behind the
 *     header so it stays legible over a photograph.
 *   - On a phone the header slides away while scrolling down and comes back
 *     on the way up (the old `navup`), so it never covers half of a small
 *     screen.
 *
 * The language dropdown is a <details> and opens by itself; this closes it on
 * Esc and on a click anywhere outside it, as a dropdown is expected to.
 */
class SiteHeader extends Component {
  static requiredRefs = ['toggle', 'menu'];

  #lastY = 0;

  connected() {
    this.on(this.refs.toggle, 'click', this.#onToggle);
    this.on(this.refs.menu, 'click', this.#onMenuClick);
    this.on(document, 'keydown', this.#onKeydown);
    this.on(document, 'click', this.#onDocumentClick);
    this.on(window, 'scroll', this.#onScroll, { passive: true });
    this.#onScroll();
  }

  get isOpen() {
    return this.hasAttribute('data-menu-open');
  }

  open() {
    this.toggleAttribute('data-menu-open', true);
    this.refs.toggle.setAttribute('aria-expanded', 'true');
    this.refs.menu.removeAttribute('inert');
    this.refs.menu.querySelector('a, button')?.focus({ preventScroll: true });
  }

  close({ restoreFocus = true } = {}) {
    if (!this.isOpen) return;
    this.removeAttribute('data-menu-open');
    this.refs.toggle.setAttribute('aria-expanded', 'false');
    this.refs.menu.setAttribute('inert', '');
    if (restoreFocus) this.refs.toggle.focus({ preventScroll: true });
  }

  #onToggle = () => {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  };

  /** A link inside the menu navigates; the menu should not still be open if
      it only jumps within the page. */
  #onMenuClick = (event) => {
    if (/** @type {Element} */ (event.target).closest('a[href]')) this.close({ restoreFocus: false });
  };

  /** @returns {HTMLDetailsElement | null} */
  get #languageSwitch() {
    return this.querySelector('[data-language-switch]');
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const languages = this.#languageSwitch;
    if (languages?.open) {
      languages.open = false;
      languages.querySelector('summary')?.focus();
    } else if (this.isOpen) {
      this.close();
    }
  };

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    const languages = this.#languageSwitch;
    if (languages?.open && !languages.contains(/** @type {Node} */ (event.target))) languages.open = false;
  };

  #onScroll = () => {
    const y = window.scrollY;
    this.toggleAttribute('data-scrolled', y > 10);

    const goingDown = y > this.#lastY && y > this.offsetHeight * 2;
    this.toggleAttribute('data-hidden', goingDown && !this.isOpen);
    this.#lastY = y;
  };
}

define('site-header', SiteHeader);
